import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Heart } from "lucide-react";
import { formatPrice, catMeta, typeLabel, regionLabel, imgUrl, PLACEHOLDER_IMAGE } from "../lib/format";
import { useLang } from "../lib/i18n";

export default function ListingCard({ listing, favorited, onToggleFav, index = 0 }) {
  const { lang } = useLang();
  const meta = catMeta(listing.category);
  const Icon = meta.icon;
  const img = imgUrl(listing.images?.[0]) || PLACEHOLDER_IMAGE;
  const isRent = listing.listing_type === "rent" || listing.category === "rentals";

  return (
    <Link
      to={`/listing/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className="group block bg-[#121212] border border-white/10 hover:border-white/30 hover:-translate-y-1 transition-all duration-300 fade-up"
      style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={img}
          alt={listing.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-2.5 py-1 border border-white/10">
          <Icon size={12} className="text-gold" />
          <span className="text-[11px] uppercase tracking-wider text-white/90">{meta[lang]}</span>
        </div>
        <span className="absolute top-3 right-3 bg-gold/90 text-black text-[10px] uppercase tracking-wider font-semibold px-2 py-1">
          {typeLabel(listing.listing_type, lang)}
        </span>
        {onToggleFav && (
          <button
            data-testid={`fav-btn-${listing.id}`}
            onClick={(e) => { e.preventDefault(); onToggleFav(listing.id); }}
            className="absolute bottom-3 right-3 w-9 h-9 bg-black/60 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:border-gold transition-colors"
          >
            <Heart size={16} className={favorited ? "fill-gold text-gold" : "text-white/80"} />
          </button>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-white font-medium leading-snug line-clamp-1 group-hover:text-gold transition-colors">{listing.title}</h3>
        <div className="flex items-center gap-1 text-white/40 text-xs mt-1.5">
          <MapPin size={12} /> <span className="line-clamp-1">{listing.city}, {regionLabel(listing.region, lang)}</span>
        </div>
        <p className="text-gold font-display text-lg mt-3">
          {formatPrice(listing.price)}{isRent && <span className="text-white/40 text-xs font-sans">/{lang === "en" ? "mo" : "mois"}</span>}
        </p>
      </div>
    </Link>
  );
}
